import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../style/EditTreatmentPage.css';
import { useTranslation } from 'react-i18next'; // Import translation hook

const EditTreatmentPage = () => {
    const { id, conditionId, treatmentId } = useParams(); // Get pet ID, condition ID, and treatment ID from URL
    const navigate = useNavigate();
    const { t } = useTranslation(); // Translation function
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const [treatmentName, setTreatmentName] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchTreatmentDetails = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}/treatments/${treatmentId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(t('condition.fetchFailed')); // Use translation for error
                }

                const data = await response.json();
                setTreatmentName(data.name); // Set the treatment name
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        fetchTreatmentDetails();
    }, [id, conditionId, treatmentId, baseUrl, t]);

    const handleUpdateTreatment = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}/treatments/${treatmentId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ name: treatmentName }), // Send only the `name` field
            });

            if (!response.ok) {
                throw new Error(t('condition.updateFailed')); // Use translation for error
            }

            navigate(`/pet/${id}/conditions/${conditionId}`); // Redirect to the condition page
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTreatment = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}/treatments/${treatmentId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(t('condition.deleteFailed')); // Use translation for error
            }

            navigate(`/pet/${id}/conditions/${conditionId}`); // Redirect to the condition page after deletion
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return <div className="error-message text-red-500 text-center">{t('general.error')}: {error}</div>;
    }

    if (loading) {
        return <div className="loading-message text-dafauna-gray text-center">{t('general.loading')}</div>;
    }

    return (
        <div className="edit-treatment-page bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="text-dafauna-darkText text-2xl font-bold text-center mb-4">{t('condition.titleEditTreatment')}</h2>
            <form onSubmit={handleUpdateTreatment} className="space-y-4">
                <div className="form-group">
                    <label htmlFor="treatmentName" className="block text-dafauna-darkText font-medium mb-2">
                        {t('general.name')}
                    </label>
                    <input
                        id="treatmentName"
                        type="text"
                        value={treatmentName}
                        onChange={(e) => setTreatmentName(e.target.value)}
                        required
                        className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    />
                </div>
                <button
                    type="submit"
                    className="btn update-treatment-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    {t('general.update')}
                </button>
                <button
                    type="button"
                    onClick={handleDeleteTreatment}
                    className="btn delete-treatment-btn w-full bg-red-500 text-white rounded-md px-4 py-2 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    disabled={loading}
                >
                    {loading ? t('general.deleting') : t('general.delete')}
                </button>
            </form>
        </div>
    );
};

export default EditTreatmentPage;